/*
 * Copyright 2020 The Yorkie Authors. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { ActorID } from '@yorkie-js/sdk/src/document/time/actor_id';
import { Comparator } from '@yorkie-js/sdk/src/util/comparator';
import { SplayNode, SplayTree } from '@yorkie-js/sdk/src/util/splay_tree';
import { LLRBTree } from '@yorkie-js/sdk/src/util/llrb_tree';
import {
  InitialTimeTicket,
  TimeTicket,
  TimeTicketSize,
  TimeTicketStruct,
} from '@yorkie-js/sdk/src/document/time/ticket';
import { VersionVector } from '@yorkie-js/sdk/src/document/time/version_vector';
import { GCChild, GCPair, GCParent } from '@yorkie-js/sdk/src/document/crdt/gc';
import { Code, YorkieError } from '@yorkie-js/sdk/src/util/error';
import {
  DataSize,
  addDataSizes,
  subDataSize,
} from '@yorkie-js/sdk/src/util/resource';

export interface ValueChange<T> {
  actor: ActorID;
  from: number;
  to: number;
  value?: T;
}

export interface RGATreeSplitValue {
  length: number;
  substring(indexStart: number, indexEnd?: number): RGATreeSplitValue;
  getDataSize(): DataSize;
}

/**
 * `RGATreeSplitPosStruct` is a structure represents the meta data of the node pos.
 * It is used to serialize and deserialize the node pos.
 */
export type RGATreeSplitPosStruct = {
  id: RGATreeSplitNodeIDStruct;
  relativeOffset: number;
};

/**
 * `RGATreeSplitNodeIDStruct` is a structure represents the meta data of the node id.
 * It is used to serialize and deserialize the node id.
 */
export type RGATreeSplitNodeIDStruct = {
  createdAt: TimeTicketStruct;
  offset: number;
};

/**
 * `RGATreeSplitNodeID` is an ID of RGATreeSplitNode.
 */
export class RGATreeSplitNodeID {
  private createdAt: TimeTicket;
  private offset: number;

  constructor(createdAt: TimeTicket, offset: number) {
    this.createdAt = createdAt;
    this.offset = offset;
  }

  /**
   * `of` creates a instance of RGATreeSplitNodeID.
   */
  public static of(createdAt: TimeTicket, offset: number): RGATreeSplitNodeID {
    return new RGATreeSplitNodeID(createdAt, offset);
  }

  /**
   * `fromStruct` creates a instance of RGATreeSplitNodeID from the struct.
   */
  public static fromStruct(
    struct: RGATreeSplitNodeIDStruct,
  ): RGATreeSplitNodeID {
    return RGATreeSplitNodeID.of(
      TimeTicket.fromStruct(struct.createdAt),
      struct.offset,
    );
  }

  /**
   * `getCreatedAt` returns the creation time of this ID.
   */
  public getCreatedAt(): TimeTicket {
    return this.createdAt;
  }

  /**
   * `getOffset` returns returns the offset of this ID.
   */
  public getOffset(): number {
    return this.offset;
  }

  /**
   * `equals` returns whether given ID equals to this ID or not.
   */
  public equals(other: RGATreeSplitNodeID): boolean {
    return (
      this.createdAt.compare(other.createdAt) === 0 &&
      this.offset === other.offset
    );
  }

  /**
   * `hasSameCreatedAt` returns whether given ID has same creation time with this ID.
   */
  public hasSameCreatedAt(other: RGATreeSplitNodeID): boolean {
    return this.createdAt.compare(other.createdAt) === 0;
  }

  /**
   * `split` creates a new ID with an offset from this ID.
   */
  public split(offset: number): RGATreeSplitNodeID {
    return new RGATreeSplitNodeID(this.createdAt, this.offset + offset);
  }

  /**
   * `toStruct` returns the structure of this node id.
   */
  public toStruct(): RGATreeSplitNodeIDStruct {
    return {
      createdAt: this.createdAt.toStruct(),
      offset: this.offset,
    };
  }

  /**
   * `toTestString` returns a String containing
   * the meta data of the node id for debugging purpose.
   */
  public toTestString(): string {
    return `${this.createdAt.toTestString()}:${this.offset}`;
  }

  /**
   * `toIDString` returns a string that can be used as an ID for this node id.
   */
  public toIDString(): string {
    return `${this.createdAt.toIDString()}:${this.offset}`;
  }
}

const InitialRGATreeSplitNodeID = RGATreeSplitNodeID.of(InitialTimeTicket, 0);

/**
 * `RGATreeSplitPos` is the position of the text inside the node.
 */
export class RGATreeSplitPos {
  private id: RGATreeSplitNodeID;
  private relativeOffset: number;

  constructor(id: RGATreeSplitNodeID, relativeOffset: number) {
    this.id = id;
    this.relativeOffset = relativeOffset;
  }

  /**
   * `of` creates a instance of RGATreeSplitPos.
   */
  public static of(
    id: RGATreeSplitNodeID,
    relativeOffset: number,
  ): RGATreeSplitPos {
    return new RGATreeSplitPos(id, relativeOffset);
  }

  /**
   * `fromStruct` creates a instance of RGATreeSplitPos from the struct.
   */
  public static fromStruct(struct: RGATreeSplitPosStruct): RGATreeSplitPos {
    const id = RGATreeSplitNodeID.fromStruct(struct.id);
    return RGATreeSplitPos.of(id, struct.relativeOffset);
  }

  /**
   * `getID` returns the ID of this RGATreeSplitPos.
   */
  public getID(): RGATreeSplitNodeID {
    return this.id;
  }

  /**
   * `getRelativeOffset` returns the relative offset of this RGATreeSplitPos.
   */
  public getRelativeOffset(): number {
    return this.relativeOffset;
  }

  /**
   * `getAbsoluteID` returns the absolute id of this RGATreeSplitPos.
   */
  public getAbsoluteID(): RGATreeSplitNodeID {
    return RGATreeSplitNodeID.of(
      this.id.getCreatedAt(),
      this.id.getOffset() + this.relativeOffset,
    );
  }

  /**
   *`toTestString` returns a String containing
   * the meta data of the position for debugging purpose.
   */
  public toTestString(): string {
    return `${this.id.toTestString()}:${this.relativeOffset}`;
  }

  /**
   * `toStruct` returns the structure of this node pos.
   */
  public toStruct(): RGATreeSplitPosStruct {
    return {
      id: this.id.toStruct(),
      relativeOffset: this.relativeOffset,
    };
  }

  /**
   * `equals` returns whether given pos equal to this pos or not.
   */
  public equals(other: RGATreeSplitPos): boolean {
    if (!this.id.equals(other.id)) {
      return false;
    }

    return this.relativeOffset === other.relativeOffset;
  }
}

export type RGATreeSplitPosRange = [RGATreeSplitPos, RGATreeSplitPos];

/**
 * `RestoreSpan` identifies a run of characters from a single original
 * insertion: the absolute-offset interval [start, end) of the insertion
 * created at `createdAt`. `value` is a deep copy of the removed content,
 * carried so that purged nodes can be recreated (GC-safe).
 */
export type RestoreSpan<T extends RGATreeSplitValue> = {
  createdAt: TimeTicket;
  start: number;
  end: number;
  value: T;
};

/**
 * `RGATreeSplitNode` is a node of RGATreeSplit.
 */
export class RGATreeSplitNode<T extends RGATreeSplitValue>
  extends SplayNode<T>
  implements GCChild
{
  private id: RGATreeSplitNodeID;
  private removedAt?: TimeTicket;

  private prev?: RGATreeSplitNode<T>;
  private next?: RGATreeSplitNode<T>;
  private insPrev?: RGATreeSplitNode<T>;
  private insNext?: RGATreeSplitNode<T>;

  constructor(id: RGATreeSplitNodeID, value?: T, removedAt?: TimeTicket) {
    super(value!);
    this.id = id;
    this.removedAt = removedAt;
  }

  /**
   * `create` creates a instance of RGATreeSplitNode.
   */
  public static create<T extends RGATreeSplitValue>(
    id: RGATreeSplitNodeID,
    value?: T,
  ): RGATreeSplitNode<T> {
    return new RGATreeSplitNode(id, value);
  }

  /**
   * `createComparator` creates a function to compare two RGATreeSplitNodeID.
   */
  public static createComparator(): Comparator<RGATreeSplitNodeID> {
    return (p1: RGATreeSplitNodeID, p2: RGATreeSplitNodeID): number => {
      const compare = p1.getCreatedAt().compare(p2.getCreatedAt());
      if (compare !== 0) {
        return compare;
      }

      if (p1.getOffset() > p2.getOffset()) {
        return 1;
      } else if (p1.getOffset() < p2.getOffset()) {
        return -1;
      }
      return 0;
    };
  }

  /**
   * `getID` returns the ID of this RGATreeSplitNode.
   */
  public getID(): RGATreeSplitNodeID {
    return this.id;
  }

  /**
   * `getCreatedAt` returns creation time of the Id of RGATreeSplitNode.
   */
  public getCreatedAt(): TimeTicket {
    return this.id.getCreatedAt();
  }

  /**
   * `getLength` returns the length of this node.
   */
  public getLength(): number {
    if (this.removedAt) {
      return 0;
    }
    return this.getContentLength();
  }

  /**
   * `getContentLength` returns the length of this value.
   */
  public getContentLength(): number {
    return (this.value && this.value.length) || 0;
  }

  /**
   * `getPrev` returns a previous node of this node.
   */
  public getPrev(): RGATreeSplitNode<T> | undefined {
    return this.prev;
  }

  /**
   * `getNext` returns a next node of this node.
   */
  public getNext(): RGATreeSplitNode<T> | undefined {
    return this.next;
  }

  /**
   * `getInsPrev` returns a previous node of this node insertion.
   */
  public getInsPrev(): RGATreeSplitNode<T> | undefined {
    return this.insPrev;
  }

  /**
   * `getInsNext` returns a next node of this node insertion.
   */
  public getInsNext(): RGATreeSplitNode<T> | undefined {
    return this.insNext;
  }

  /**
   * `getInsPrevID` returns a ID of previous node insertion.
   */
  public getInsPrevID(): RGATreeSplitNodeID {
    return this.insPrev!.getID();
  }

  /**
   * `setPrev` sets previous node of this node.
   */
  public setPrev(node?: RGATreeSplitNode<T>): void {
    this.prev = node;
    if (node) {
      node.next = this;
    }
  }

  /**
   * `setNext` sets next node of this node.
   */
  public setNext(node?: RGATreeSplitNode<T>): void {
    this.next = node;
    if (node) {
      node.prev = this;
    }
  }

  /**
   * `setInsPrev` sets previous node of this node insertion.
   */
  public setInsPrev(node?: RGATreeSplitNode<T>): void {
    this.insPrev = node;
    if (node) {
      node.insNext = this;
    }
  }

  /**
   * `setInsNext` sets next node of this node insertion.
   */
  public setInsNext(node?: RGATreeSplitNode<T>): void {
    this.insNext = node;
    if (node) {
      node.insPrev = this;
    }
  }

  /**
   * `hasNext` checks if next node exists.
   */
  public hasNext(): boolean {
    return !!this.next;
  }

  /**
   * `hasInsPrev` checks if previous insertion node exists.
   */
  public hasInsPrev(): boolean {
    return !!this.insPrev;
  }

  /**
   * `isRemoved` checks if removed time exists.
   */
  public isRemoved(): boolean {
    return !!this.removedAt;
  }

  /**
   * `getRemovedAt` returns the remove time of this node.
   */
  public getRemovedAt(): TimeTicket | undefined {
    return this.removedAt;
  }

  /**
   * `split` creates a new split node of the given offset.
   */
  public split(offset: number): RGATreeSplitNode<T> {
    return new RGATreeSplitNode(
      this.id.split(offset),
      this.splitValue(offset),
      this.removedAt,
    );
  }

  /**
   * `canDelete` checks if node is able to delete.
   */
  public canRemove(
    editedAt: TimeTicket,
    creationKnown: boolean,
    tombstoneKnown: boolean,
  ): boolean {
    // NOTE(hackerwins): Skip if the node's creation was not visible to this
    // operation.
    if (!creationKnown) {
      return false;
    }

    if (!this.removedAt) {
      return true;
    }

    // NOTE(hackerwins): Overwrite only if prior tombstone was not known
    // (concurrent or unseen) and newer. This enables LWW for concurrent deletions.
    if (!tombstoneKnown && editedAt?.after(this.removedAt)) {
      return true;
    }
    return false;
  }

  /**
   * `canStyle` checks if node is able to set style.
   */
  public canStyle(
    editedAt: TimeTicket,
    clientLamportAtChange: bigint,
  ): boolean {
    const nodeExisted =
      this.getCreatedAt().getLamport() <= clientLamportAtChange;

    return nodeExisted && (!this.removedAt || editedAt.after(this.removedAt));
  }

  /**
   * `setRemovedAt` sets the remove time of this node.
   */
  public setRemovedAt(removedAt?: TimeTicket): void {
    this.removedAt = removedAt;
  }

  /**
   * `remove` removes the node of the given edited time.
   */
  public remove(removedAt: TimeTicket) {
    if (!this.removedAt) {
      this.removedAt = removedAt;
      return;
    }

    // NOTE: Overwrite only if newer. Callers must ensure overwrite is valid
    // (e.g., prior tombstone was not known) by checking canRemove beforehand.
    if (removedAt.after(this.removedAt)) {
      this.removedAt = removedAt;
    }
  }

  /**
   * `createRange` creates ranges of RGATreeSplitPos.
   */
  public createPosRange(): RGATreeSplitPosRange {
    return [
      RGATreeSplitPos.of(this.id, 0),
      RGATreeSplitPos.of(this.id, this.getLength()),
    ];
  }

  /**
   * `getData` returns the data of this node.
   */
  public getDataSize(): DataSize {
    const dataSize = this.value.getDataSize();

    if (this.id) {
      dataSize.meta += TimeTicketSize;
    }

    if (this.removedAt) {
      dataSize.meta += TimeTicketSize;
    }

    return dataSize;
  }

  /**
   * `deepcopy` returns a new instance of this RGATreeSplitNode without structural info.
   */
  public deepcopy(): RGATreeSplitNode<T> {
    return new RGATreeSplitNode(this.id, this.value, this.removedAt);
  }

  /**
   * `toTestString` returns a String containing
   * the meta data of the node for debugging purpose.
   */
  public toTestString(): string {
    return `${this.id.toTestString()} ${this.value ? this.value : ''}`;
  }

  private splitValue(offset: number): T {
    const value = this.value;
    this.value = value.substring(0, offset) as T;
    return value.substring(offset, value.length) as T;
  }

  /**
   * `toIDString` returns a string that can be used as an ID for this position.
   */
  public toIDString(): string {
    return this.id.toIDString();
  }
}

/**
 * `RGATreeSplit` is a block-based list with improved index-based lookup in RGA.
 * The difference from RGATreeList is that it has data on a block basis to
 * reduce the size of CRDT metadata. When an edit occurs on a block,
 * the block is split.
 *
 */
export class RGATreeSplit<T extends RGATreeSplitValue> implements GCParent {
  private head: RGATreeSplitNode<T>;
  private treeByIndex: SplayTree<T>;
  private treeByID: LLRBTree<RGATreeSplitNodeID, RGATreeSplitNode<T>>;

  /**
   * `pendingGCPairs` buffers GC pairs for nodes that were created
   * already-tombstoned by splitting a removed node. Such pieces inherit
   * `removedAt` without ever passing through `remove()`, so they would
   * otherwise never be registered for GC. Callers that split nodes
   * (`edit`, `CRDTText.setStyle`, `CRDTText.removeStyle`) drain this
   * buffer into their returned GC pairs.
   */
  private pendingGCPairs: Array<GCPair>;

  constructor() {
    this.head = RGATreeSplitNode.create(InitialRGATreeSplitNodeID);
    this.treeByIndex = new SplayTree();
    this.treeByID = new LLRBTree(RGATreeSplitNode.createComparator());
    this.pendingGCPairs = [];

    this.treeByIndex.insert(this.head);
    this.treeByID.put(this.head.getID(), this.head);
  }

  /**
   * `create` creates a instance RGATreeSplit.
   */
  public static create<T extends RGATreeSplitValue>(): RGATreeSplit<T> {
    return new RGATreeSplit();
  }

  /**
   * `edit` does following steps
   * 1. split nodes with from and to
   * 2. delete between from and to
   * 3. insert a new node
   * 4. add removed node
   * @param range - range of RGATreeSplitNode
   * @param editedAt - edited time
   * @param value - value
   * @returns `[RGATreeSplitPos, Array<GCPair>, DataSize, Array<ValueChange<T>>, Array<T>]`
   */
  public edit(
    range: RGATreeSplitPosRange,
    editedAt: TimeTicket,
    value?: T,
    versionVector?: VersionVector,
  ): [
    RGATreeSplitPos,
    Array<GCPair>,
    DataSize,
    Array<ValueChange<T>>,
    Array<T>,
    Array<RestoreSpan<T>>,
  ] {
    const diff = { data: 0, meta: 0 };

    // 01. split nodes with from and to
    const [toLeft, diffTo, toRight] = this.findNodeWithSplit(
      range[1],
      editedAt,
    );
    const [fromLeft, diffFrom, fromRight] = this.findNodeWithSplit(
      range[0],
      editedAt,
    );

    addDataSizes(diff, diffTo, diffFrom);

    // 02. delete between from and to
    const nodesToDelete = this.findBetween(fromRight, toRight);
    const [changes, removedNodes, alreadyRemovedIDs] = this.deleteNodes(
      nodesToDelete,
      editedAt,
      versionVector,
    );

    const caretID = toRight ? toRight.getID() : toLeft.getID();
    let caretPos = RGATreeSplitPos.of(caretID, 0);

    // 03. insert a new node
    if (value) {
      const idx = this.posToIndex(fromLeft.createPosRange()[1], true);

      const inserted = this.insertAfter(
        fromLeft,
        RGATreeSplitNode.create(RGATreeSplitNodeID.of(editedAt, 0), value),
      );
      addDataSizes(diff, inserted.getDataSize());

      if (changes.length && changes[changes.length - 1].from === idx) {
        changes[changes.length - 1].value = value;
      } else {
        changes.push({
          actor: editedAt.getActorID(),
          from: idx,
          to: idx,
          value,
        });
      }

      caretPos = RGATreeSplitPos.of(
        inserted.getID(),
        inserted.getContentLength(),
      );
    }

    // 04. add removed node
    const pairs: Array<GCPair> = [];
    const removedValues: Array<T> = [];
    const removedSpans: Array<RestoreSpan<T>> = []; // NEW
    for (const [id, removedNode] of removedNodes) {
      // NOTE: Nodes that were already tombstoned keep their existing GC
      // pair (the pair reads `removedAt` from the node at collection
      // time); re-registering would toggle the pair off and leak the node.
      if (!alreadyRemovedIDs.has(id)) {
        pairs.push({ parent: this, child: removedNode });
      }
      removedValues.push(removedNode.getValue());
      // NEW: capture split-invariant character identities. substring(0)
      // deep-copies the value so later splits of the tombstone cannot
      // mutate the captured content.
      removedSpans.push({
        createdAt: removedNode.getCreatedAt(),
        start: removedNode.getID().getOffset(),
        end: removedNode.getID().getOffset() + removedNode.getContentLength(),
        value: removedNode.getValue().substring(0) as T,
      });
    }

    pairs.push(...this.drainPendingGCPairs());

    return [caretPos, pairs, diff, changes, removedValues, removedSpans];
  }

  /**
   * `restore` re-establishes the characters described by `spans` under
   * their ORIGINAL identities. For each span, per overlapping region:
   *   - live piece exists       → skip (idempotent; another undo restored it)
   *   - tombstoned piece exists → clear removedAt (un-tombstone)
   *   - no piece exists (GC'd)  → recreate a node with the original ID
   *
   * Returns [untombstonedNodes, recreatedNodes, changes, liveDiff,
   * pendingGCPairs]. `changes` describes the revived content as insertions
   * (ascending index) so editor bindings and remote sync can be driven
   * the same way as a normal edit.
   *
   * The caller must, in order: (1) register every pair in `pendingGCPairs`
   * — these are fragments `splitNode` buffered while isolating a target
   * range out of a larger tombstoned piece (see `drainPendingGCPairs`);
   * (2) unregister GC pairs for `untombstonedNodes`. Registering first is
   * required for `untombstonedNodes` entries whose node was itself one of
   * those split-born fragments (a target isolated from the interior of a
   * tombstone) — such a node was never registered under its own id, so
   * step (1) creates the entry that step (2) then correctly walks from gc
   * back to live; entries that remain tombstoned (siblings of the
   * restored target) simply stay registered. Finally, `root.acc(liveDiff)`
   * accounts the size of any nodes recreated from scratch (the GC'd-away
   * case), which `splitNode`'s buffering does not cover.
   */
  public restore(
    spans: Array<RestoreSpan<T>>,
    executedAt: TimeTicket,
    fallbackAnchor?: RGATreeSplitPos,
  ): [
    Array<RGATreeSplitNode<T>>,
    Array<RGATreeSplitNode<T>>,
    Array<ValueChange<T>>,
    DataSize,
    Array<GCPair>,
  ] {
    const untombstoned: Array<RGATreeSplitNode<T>> = [];
    const recreated: Array<RGATreeSplitNode<T>> = [];
    const liveDiff = { data: 0, meta: 0 };

    // The last node placed at the current cursor (un-tombstoned or recreated),
    // in document order. When a recreated fragment has no surviving same-
    // insertion anchor, chaining after this keeps a multi-fragment run in
    // left-to-right order instead of each fragment prepending at the same fixed
    // fallback anchor — which would rebuild the run reversed. Spans arrive in
    // document order, so this is always the recreated fragment's left neighbour.
    let chainAnchor: RGATreeSplitNode<T> | undefined;
    for (const span of spans) {
      const pieces = this.findPiecesOverlapping(
        span.createdAt,
        span.start,
        span.end,
      );

      let cursor = span.start;
      let pieceIdx = 0;
      while (cursor < span.end) {
        const piece = pieces[pieceIdx];
        const pieceStart = piece ? piece.getID().getOffset() : Infinity;
        const pieceEnd = piece
          ? pieceStart + piece.getContentLength()
          : Infinity;

        if (piece && pieceStart <= cursor) {
          // Covered by an existing piece.
          const overlapEnd = Math.min(pieceEnd, span.end);
          if (piece.isRemoved()) {
            const [target] = this.isolateRange(piece, cursor, overlapEnd);
            target.setRemovedAt(undefined);
            // Repair splay weights on the path to root (length 0 → len).
            this.treeByIndex.splayNode(target);
            untombstoned.push(target);
            chainAnchor = target;
          } else {
            chainAnchor = piece;
          }
          cursor = overlapEnd;
          if (overlapEnd >= pieceEnd) {
            pieceIdx++;
          }
        } else {
          // Gap: recreate [cursor, gapEnd) with its original ID.
          const gapEnd = Math.min(pieceStart, span.end);
          const value = span.value.substring(
            cursor - span.start,
            gapEnd - span.start,
          ) as T;
          const newNode = RGATreeSplitNode.create(
            RGATreeSplitNodeID.of(span.createdAt, cursor),
            value,
          );
          addDataSizes(liveDiff, newNode.getDataSize());
          const prev = this.findRestoreAnchor(
            span.createdAt,
            cursor,
            gapEnd,
            executedAt,
            fallbackAnchor,
            chainAnchor,
          );
          this.insertAfter(prev, newNode);
          recreated.push(newNode);
          chainAnchor = newNode;
          cursor = gapEnd;
        }
      }
    }

    const pendingGCPairs = this.drainPendingGCPairs();

    // Revived nodes are now live; report each as an insertion at its final
    // index. Ascending order keeps the indices valid when applied in
    // sequence (each earlier insertion is already present).
    const changes: Array<ValueChange<T>> = [];
    for (const node of [...untombstoned, ...recreated]) {
      const [from] = this.findIndexesFromRange(node.createPosRange());
      changes.push({
        actor: executedAt.getActorID(),
        from,
        to: from,
        value: node.getValue(),
      });
    }
    changes.sort((a, b) => a.from - b.from);

    return [untombstoned, recreated, changes, liveDiff, pendingGCPairs];
  }

  /**
   * `retombstone` re-deletes the characters described by `spans` (redo of
   * an identity-preserving undo). Only live pieces are affected; already
   * removed or purged regions are skipped (idempotent).
   *
   * Returns [pairs, changes, diff]: GCPairs for the newly tombstoned
   * nodes, the removed regions as deletions so editor bindings and remote
   * sync can be driven the same way as a normal edit, and the
   * metadata-size overhead from splitting the (live) pieces to isolate the
   * target range. The caller must `root.acc(diff)` before registering
   * `pairs`, mirroring how a normal edit's boundary splits are accounted
   * before its resulting tombstones are registered. Indices are captured
   * before each removal, so applying them in emission order stays
   * consistent.
   */
  public retombstone(
    spans: Array<RestoreSpan<T>>,
    executedAt: TimeTicket,
  ): [Array<GCPair>, Array<ValueChange<T>>, DataSize] {
    const pairs: Array<GCPair> = [];
    const changes: Array<ValueChange<T>> = [];
    const diff = { data: 0, meta: 0 };

    for (const span of spans) {
      const pieces = this.findPiecesOverlapping(
        span.createdAt,
        span.start,
        span.end,
      );
      for (const piece of pieces) {
        if (piece.isRemoved()) {
          continue;
        }
        const pieceStart = piece.getID().getOffset();
        const pieceEnd = pieceStart + piece.getContentLength();
        const [target, splitDiff] = this.isolateRange(
          piece,
          Math.max(pieceStart, span.start),
          Math.min(pieceEnd, span.end),
        );
        // `piece` was live, so the split overhead belongs to the live
        // bucket, same as a normal edit's boundary splits.
        addDataSizes(diff, splitDiff);
        // Capture the visible range while `target` is still live.
        const [from, to] = this.findIndexesFromRange(target.createPosRange());
        target.remove(executedAt);
        this.treeByIndex.splayNode(target);
        pairs.push({ parent: this, child: target });
        if (from < to) {
          changes.push({ actor: executedAt.getActorID(), from, to });
        }
      }
    }

    // Defensive: retombstone only ever isolates live pieces, so splitNode
    // never buffers anything here — drain anyway to stay consistent with
    // every other caller of isolateRange/splitNode.
    pairs.push(...this.drainPendingGCPairs());

    return [pairs, changes, diff];
  }

  /**
   * `findPiecesOverlapping` collects existing nodes (live or tombstoned)
   * belonging to the insertion `createdAt` that overlap the absolute-offset
   * interval [start, end), in ascending offset order. Works by descending
   * floorEntry probes over treeByID.
   */
  private findPiecesOverlapping(
    createdAt: TimeTicket,
    start: number,
    end: number,
  ): Array<RGATreeSplitNode<T>> {
    const pieces: Array<RGATreeSplitNode<T>> = [];
    let probe = end - 1;

    while (probe >= 0) {
      const key = RGATreeSplitNodeID.of(createdAt, probe);
      const entry = this.treeByID.floorEntry(key);
      if (!entry || !entry.key.hasSameCreatedAt(key)) {
        break;
      }
      const node = entry.value;
      const nodeStart = node.getID().getOffset();
      const nodeEnd = nodeStart + node.getContentLength();
      if (nodeEnd <= start) {
        break;
      }
      if (nodeStart < end && nodeEnd > start) {
        pieces.push(node);
      }
      if (nodeStart <= start) {
        break;
      }
      probe = nodeStart - 1;
    }

    return pieces.reverse();
  }

  /**
   * `findPieceCovering` returns the node of insertion `createdAt` whose
   * absolute-offset range covers `offset`, if present.
   */
  private findPieceCovering(
    createdAt: TimeTicket,
    offset: number,
  ): RGATreeSplitNode<T> | undefined {
    const key = RGATreeSplitNodeID.of(createdAt, offset);
    const entry = this.treeByID.floorEntry(key);
    if (!entry || !entry.key.hasSameCreatedAt(key)) {
      return;
    }
    const node = entry.value;
    const nodeStart = node.getID().getOffset();
    const nodeEnd = nodeStart + node.getContentLength();
    if (nodeStart <= offset && offset < nodeEnd) {
      return node;
    }
    return;
  }

  /**
   * `findRestoreAnchor` returns the physical node to insert a recreated
   * fragment [gapStart, gapEnd) of insertion `createdAt` AFTER.
   *
   * Resolution ladder (all rules key on op-carried data + ID lookups only):
   *  (a) a piece covering gapEnd exists → directly before it
   *      (originally-adjacent successor; exact original slot)
   *  (b) nearest surviving piece of the same insertion left of gapStart
   *      → directly after it
   *  (c) rightmost surviving piece of the same insertion (must be right
   *      of the gap) → directly before it
   *  (d) chain anchor: the previously placed fragment of this same restore
   *      (document order) → after it, so a purged multi-fragment run is
   *      rebuilt left-to-right rather than reversed
   *  (e) the operation's fallback anchor (refined) — same exposure as the
   *      current implementation's fromPos; see design-doc caveat
   *  (f) head (deterministic last resort)
   */
  private findRestoreAnchor(
    createdAt: TimeTicket,
    gapStart: number,
    gapEnd: number,
    executedAt: TimeTicket,
    fallbackAnchor?: RGATreeSplitPos,
    chainAnchor?: RGATreeSplitNode<T>,
  ): RGATreeSplitNode<T> {
    const succ = this.findPieceCovering(createdAt, gapEnd);
    if (succ) {
      return succ.getPrev()!;
    }

    if (gapStart > 0) {
      const key = RGATreeSplitNodeID.of(createdAt, gapStart - 1);
      const entry = this.treeByID.floorEntry(key);
      if (entry && entry.key.hasSameCreatedAt(key)) {
        return entry.value;
      }
    }

    const rightmostKey = RGATreeSplitNodeID.of(
      createdAt,
      Number.MAX_SAFE_INTEGER,
    );
    const rightmost = this.treeByID.floorEntry(rightmostKey);
    if (
      rightmost &&
      rightmost.key.hasSameCreatedAt(rightmostKey) &&
      rightmost.value.getID().getOffset() >= gapEnd
    ) {
      return rightmost.value.getPrev()!;
    }

    // (d) No surviving piece of this insertion anchors the fragment. When the
    // whole run was purged, every fragment lands here; anchoring after the
    // fragment placed just before it (document order) keeps the run forward.
    if (chainAnchor) {
      return chainAnchor;
    }

    if (fallbackAnchor) {
      try {
        const refined = this.refinePos(fallbackAnchor);
        const [left] = this.findNodeWithSplit(refined, executedAt);
        return left;
      } catch {
        // anchor fully purged — fall through to (e)
      }
    }

    return this.head;
  }

  /**
   * `isolateRange` splits `piece` so that a node exactly covering the
   * absolute-offset interval [from, to) exists, and returns it along with
   * the net metadata-size overhead the split(s) introduced.
   *
   * When `piece` is live, this overhead is a normal live-bucket cost (same
   * as any other boundary split) and the caller should `root.acc` it.
   * When `piece` is tombstoned, `splitNode` itself buffers the overhead of
   * any born-removed fragment via `pendingGCPairs` (see `drainPendingGCPairs`),
   * so the returned diff is zero in that case — the caller must still
   * drain and register those pairs.
   *
   * Requires: pieceStart <= from < to <= pieceEnd.
   */
  private isolateRange(
    piece: RGATreeSplitNode<T>,
    from: number,
    to: number,
  ): [RGATreeSplitNode<T>, DataSize] {
    const diff = { data: 0, meta: 0 };
    let node = piece;
    const nodeStart = node.getID().getOffset();
    if (from > nodeStart) {
      const [right, splitDiff] = this.splitNode(node, from - nodeStart);
      addDataSizes(diff, splitDiff);
      node = right as RGATreeSplitNode<T>;
    }
    const newStart = node.getID().getOffset();
    if (to < newStart + node.getContentLength()) {
      const [, splitDiff] = this.splitNode(node, to - newStart);
      addDataSizes(diff, splitDiff);
    }
    return [node, diff];
  }

  /**
   * `indexToPos` finds RGATreeSplitPos of given offset.
   */
  public indexToPos(idx: number): RGATreeSplitPos {
    const [node, offset] = this.treeByIndex.findForText(idx);
    const splitNode = node as RGATreeSplitNode<T>;
    return RGATreeSplitPos.of(splitNode.getID(), offset);
  }

  /**
   * `findIndexesFromRange` finds indexes based on range.
   */
  public findIndexesFromRange(range: RGATreeSplitPosRange): [number, number] {
    const [fromPos, toPos] = range;
    return [this.posToIndex(fromPos, false), this.posToIndex(toPos, true)];
  }

  /**
   * `posToIndex` converts the given position to index.
   */
  public posToIndex(pos: RGATreeSplitPos, preferToLeft: boolean): number {
    const absoluteID = pos.getAbsoluteID();
    const node = preferToLeft
      ? this.findFloorNodePreferToLeft(absoluteID)
      : this.findFloorNode(absoluteID);
    if (!node) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        `the node of the given id should be found: ${absoluteID.toTestString()}`,
      );
    }
    const index = this.treeByIndex.indexOf(node!);
    const offset = node!.isRemoved()
      ? 0
      : absoluteID.getOffset() - node!.getID().getOffset();
    return index + offset;
  }

  /**
   * `findNode` finds node of given id.
   */
  public findNode(id: RGATreeSplitNodeID): RGATreeSplitNode<T> {
    return this.findFloorNode(id)!;
  }

  /**
   * `length` returns size of RGATreeSplit.
   */
  public get length(): number {
    return this.treeByIndex.length;
  }

  /**
   * `getTreeByIndex` returns the tree by index for debugging purpose.
   */
  public getTreeByIndex(): SplayTree<T> {
    return this.treeByIndex;
  }

  /**
   * `getTreeByID` returns the tree by ID for debugging purpose.
   */
  public getTreeByID(): LLRBTree<RGATreeSplitNodeID, RGATreeSplitNode<T>> {
    return this.treeByID;
  }

  /**
   * `toString` returns the string encoding of this RGATreeSplit.
   */
  public toString(): string {
    const str = [];

    for (const node of this) {
      if (!node.isRemoved()) {
        str.push(node.getValue());
      }
    }

    return str.join('');
  }

  // eslint-disable-next-line jsdoc/require-jsdoc
  public *[Symbol.iterator](): IterableIterator<RGATreeSplitNode<T>> {
    let node = this.head.getNext();
    while (node) {
      yield node;
      node = node.getNext();
    }
  }

  /**
   * `getHead` returns head of RGATreeSplitNode.
   */
  public getHead(): RGATreeSplitNode<T> {
    return this.head;
  }

  /**
   * `deepcopy` copies itself deeply.
   */
  public deepcopy(): RGATreeSplit<T> {
    const clone = new RGATreeSplit<T>();

    let node = this.head.getNext();

    let prev = clone.head;
    let current;
    while (node) {
      current = clone.insertAfter(prev, node.deepcopy());
      if (node.hasInsPrev()) {
        const insPrevNode = clone.findNode(node.getInsPrevID());
        current.setInsPrev(insPrevNode);
      }

      prev = current;
      node = node.getNext();
    }

    return clone;
  }

  /**
   * `normalizePos` converts a local position `(id, rel)` into a single
   * absolute offset measured from the head `(0:0)` of the physical chain.
   */
  public normalizePos(pos: RGATreeSplitPos): RGATreeSplitPos {
    const node = this.findFloorNode(pos.getID());
    if (!node) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        `the node of the given id should be found: ${pos.getID().toTestString()}`,
      );
    }

    let total = pos.getRelativeOffset();
    let curr = node;
    let prev = node.getPrev();

    while (prev) {
      total += prev.getLength();
      curr = prev;
      prev = prev.getPrev();
    }

    return RGATreeSplitPos.of(curr.getID(), total);
  }

  /**
   * `refinePos` remaps the given pos to the current split chain.
   *
   * - Traverses the physical `next` chain (not `insNext`).
   * - Counts only live characters: removed nodes are treated as length 0.
   * - If the given offset exceeds the length of the current node,
   *   it moves forward through `next` nodes, subtracting lengths,
   *   until the offset fits in a live node.
   * - If it runs out of nodes, it snaps to the end of the last node.
   *
   * Example:
   *   Before split: ["12345"](1:2:0), pos = (1:2:0, rel=5)
   *   After split : ["1"](1:2:0) - ["23"](1:2:1) - ["45"](1:2:3)
   *   refinePos(pos) -> (1:2:3, rel=2)
   *
   * Example:
   *   ["12"](1:2:0, live) and pos = (1:2:0, rel=4)
   *   refinePos(pos) -> points two chars after "12",
   *   i.e. advances into following nodes, skipping removed ones.
   */
  public refinePos(pos: RGATreeSplitPos): RGATreeSplitPos {
    let node = this.findFloorNode(pos.getID());
    if (!node) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        `the node of the given id should be found: ${pos.getID().toTestString()}`,
      );
    }

    let offsetInPart = pos.getRelativeOffset();
    let partLen = node.getContentLength();

    while (offsetInPart > partLen) {
      offsetInPart -= partLen;
      const next: RGATreeSplitNode<T> | undefined = node!.getNext();

      if (!next) {
        return RGATreeSplitPos.of(node.getID(), partLen);
      }

      node = next;
      partLen = node.getLength();
    }

    return RGATreeSplitPos.of(node.getID(), offsetInPart);
  }

  /**
   * `toTestString` returns a String containing the meta data of the node
   * for debugging purpose.
   */
  public toTestString(): string {
    const result = [];

    let node: RGATreeSplitNode<T> | undefined = this.head;
    while (node) {
      if (node.isRemoved()) {
        result.push(`{${node.toTestString()}}`);
      } else {
        result.push(`[${node.toTestString()}]`);
      }

      node = node.getNext();
    }

    return result.join('');
  }

  /**
   * `insertAfter` inserts the given node after the given previous node.
   */
  public insertAfter(
    prevNode: RGATreeSplitNode<T>,
    newNode: RGATreeSplitNode<T>,
  ): RGATreeSplitNode<T> {
    const next = prevNode.getNext();
    newNode.setPrev(prevNode);
    if (next) {
      next.setPrev(newNode);
    }

    this.treeByID.put(newNode.getID(), newNode);
    this.treeByIndex.insertAfter(prevNode, newNode);

    return newNode;
  }

  /**
   * `findNodeWithSplit` splits and return nodes of the given position.
   */
  public findNodeWithSplit(
    pos: RGATreeSplitPos,
    editedAt: TimeTicket,
  ): [RGATreeSplitNode<T>, DataSize, RGATreeSplitNode<T>] {
    const absoluteID = pos.getAbsoluteID();
    let node = this.findFloorNodePreferToLeft(absoluteID);
    const relativeOffset = absoluteID.getOffset() - node.getID().getOffset();

    const [, diff] = this.splitNode(node, relativeOffset);

    while (node.hasNext() && node.getNext()!.getCreatedAt().after(editedAt)) {
      node = node.getNext()!;
    }

    return [node, diff, node.getNext()!];
  }

  private findFloorNodePreferToLeft(
    id: RGATreeSplitNodeID,
  ): RGATreeSplitNode<T> {
    let node = this.findFloorNode(id);
    if (!node) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        `the node of the given id should be found: ${id.toTestString()}`,
      );
    }

    if (id.getOffset() > 0 && node!.getID().getOffset() == id.getOffset()) {
      // NOTE: InsPrev may not be present due to GC.
      if (!node!.hasInsPrev()) {
        return node!;
      }
      node = node!.getInsPrev();
    }

    return node!;
  }

  private findFloorNode(
    id: RGATreeSplitNodeID,
  ): RGATreeSplitNode<T> | undefined {
    const entry = this.treeByID.floorEntry(id);
    if (!entry) {
      return;
    }

    if (!entry.key.equals(id) && !entry.key.hasSameCreatedAt(id)) {
      return;
    }

    return entry.value;
  }

  /**
   * `findBetween` returns nodes between fromNode and toNode.
   */
  public findBetween(
    fromNode: RGATreeSplitNode<T>,
    toNode: RGATreeSplitNode<T>,
  ): Array<RGATreeSplitNode<T>> {
    const nodes = [];

    let current: RGATreeSplitNode<T> | undefined = fromNode;
    while (current && current !== toNode) {
      nodes.push(current);
      current = current.getNext();
    }

    return nodes;
  }

  private splitNode(
    node: RGATreeSplitNode<T>,
    offset: number,
  ): [RGATreeSplitNode<T> | undefined, DataSize] {
    const diff = { data: 0, meta: 0 };

    if (offset > node.getContentLength()) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        `offset should be less than or equal to length`,
      );
    }

    if (offset === 0) {
      return [node, diff];
    } else if (offset === node.getContentLength()) {
      return [node.getNext(), diff];
    }

    const prvSize = node.getDataSize();

    const splitNode = node.split(offset);
    this.treeByIndex.updateWeight(splitNode);
    this.insertAfter(node, splitNode);

    const insNext = node.getInsNext();
    if (insNext) {
      insNext.setInsPrev(splitNode);
    }
    splitNode.setInsPrev(node);

    // NOTE(hackerwins): Calculate data size after node splitting:
    // Take the sum of the two split nodes(left and right) minus the size of
    // the original node. This calculates the net metadata overhead added by
    // the split operation.
    addDataSizes(diff, node.getDataSize(), splitNode.getDataSize());
    subDataSize(diff, prvSize);

    // NOTE: A piece split off an already-tombstoned node inherits
    // `removedAt` without going through `remove()`, so no GC pair is
    // created for it in the normal deletion path. Buffer one here so it
    // can be purged; otherwise it stays in the list forever. The piece
    // was never live, so the net-new size created by the split goes
    // straight to docSize.gc when the pair is registered; report a zero
    // diff to the caller (which accounts diffs to docSize.live).
    if (splitNode.isRemoved()) {
      this.pendingGCPairs.push({
        parent: this,
        child: splitNode,
        gcOnlySize: diff,
      });
      return [splitNode, { data: 0, meta: 0 }];
    }

    return [splitNode, diff];
  }

  /**
   * `drainPendingGCPairs` returns the GC pairs buffered for born-tombstoned
   * split pieces and clears the buffer.
   */
  public drainPendingGCPairs(): Array<GCPair> {
    const pairs = this.pendingGCPairs;
    this.pendingGCPairs = [];
    return pairs;
  }

  private deleteNodes(
    candidates: Array<RGATreeSplitNode<T>>,
    editedAt: TimeTicket,
    vector?: VersionVector,
  ): [Array<ValueChange<T>>, Map<string, RGATreeSplitNode<T>>, Set<string>] {
    if (!candidates.length) {
      return [[], new Map(), new Set()];
    }

    const isLocal = vector === undefined || vector.size() === 0;

    // 01. Collect nodes to remove and keep.
    const nodesToRemove: Array<RGATreeSplitNode<T>> = [];
    const nodesToKeep: Array<RGATreeSplitNode<T> | undefined> = [];
    const [leftEdge, rightEdge] = this.findEdgesOfCandidates(candidates);
    nodesToKeep.push(leftEdge);
    for (const node of candidates) {
      let creationKnown = false;
      if (isLocal) {
        creationKnown = true;
      } else {
        const createdAtVV = vector?.get(node.getCreatedAt().getActorID());
        if (createdAtVV && createdAtVV >= node.getCreatedAt().getLamport()) {
          creationKnown = true;
        }
      }

      let tombstoneKnown = false;
      if (node.getRemovedAt()) {
        const removedAtVV = vector?.get(node.getRemovedAt()!.getActorID());
        if (isLocal) {
          tombstoneKnown = true;
        } else if (
          removedAtVV &&
          removedAtVV >= node.getRemovedAt()!.getLamport()
        ) {
          tombstoneKnown = true;
        }
      }

      if (node.canRemove(editedAt, creationKnown, tombstoneKnown)) {
        nodesToRemove.push(node);
      } else {
        nodesToKeep.push(node);
      }
    }
    nodesToKeep.push(rightEdge);

    // 02. Create value changes with previous indexes before deletion.
    const changes = this.makeChanges(nodesToKeep, editedAt);

    // 03. Mark tombstones for removal. Nodes that were already removed
    // (concurrent LWW overwrite of an existing tombstone) are tracked
    // separately: they already have a registered GC pair, and registering
    // a second one would toggle-unregister the first.
    const removedNodes = new Map();
    const alreadyRemovedIDs = new Set<string>();
    for (const node of nodesToRemove) {
      const id = node.getID().toIDString();
      if (node.isRemoved()) {
        alreadyRemovedIDs.add(id);
      }
      removedNodes.set(id, node);
      node.remove(editedAt);
    }

    // 04. Clear the index tree of the given deletion boundaries.
    this.deleteIndexNodes(nodesToKeep);

    return [changes, removedNodes, alreadyRemovedIDs];
  }

  /**
   * `findEdgesOfCandidates` finds the edges outside `candidates`,
   * (which has not already been deleted, or be undefined but not yet implemented)
   * right edge is undefined means `candidates` contains the end of text.
   */
  private findEdgesOfCandidates(
    candidates: Array<RGATreeSplitNode<T>>,
  ): [RGATreeSplitNode<T>, RGATreeSplitNode<T> | undefined] {
    return [
      candidates[0].getPrev()!,
      candidates[candidates.length - 1].getNext(),
    ];
  }

  private makeChanges(
    boundaries: Array<RGATreeSplitNode<T> | undefined>,
    editedAt: TimeTicket,
  ): Array<ValueChange<T>> {
    const changes: Array<ValueChange<T>> = [];
    let fromIdx: number, toIdx: number;

    for (let i = 0; i < boundaries.length - 1; i++) {
      const leftBoundary = boundaries[i];
      const rightBoundary = boundaries[i + 1];

      if (leftBoundary!.getNext() == rightBoundary) {
        continue;
      }

      [fromIdx] = this.findIndexesFromRange(
        leftBoundary!.getNext()!.createPosRange(),
      );
      if (rightBoundary) {
        [, toIdx] = this.findIndexesFromRange(
          rightBoundary.getPrev()!.createPosRange(),
        );
      } else {
        toIdx = this.treeByIndex.length;
      }

      if (fromIdx < toIdx) {
        changes.push({
          actor: editedAt.getActorID(),
          from: fromIdx,
          to: toIdx,
        });
      }
    }

    return changes.reverse();
  }

  /**
   * `deleteIndexNodes` clears the index nodes of the given deletion boundaries.
   * The boundaries mean the nodes that will not be deleted in the range.
   */
  private deleteIndexNodes(
    boundaries: Array<RGATreeSplitNode<T> | undefined>,
  ): void {
    for (let i = 0; i < boundaries.length - 1; i++) {
      const leftBoundary = boundaries[i];
      const rightBoundary = boundaries[i + 1];
      // If there is no node to delete between boundaries, do notting.
      if (leftBoundary!.getNext() != rightBoundary) {
        this.treeByIndex.deleteRange(leftBoundary!, rightBoundary);
      }
    }
  }

  /**
   * `purge` physically purges the given node from RGATreeSplit.
   */
  public purge(node: RGATreeSplitNode<T>): void {
    this.treeByIndex.delete(node);
    this.treeByID.remove(node.getID());

    const prev = node.getPrev();
    const next = node.getNext();
    const insPrev = node.getInsPrev();
    const insNext = node.getInsNext();

    if (prev) {
      prev.setNext(next);
    }
    if (next) {
      next.setPrev(prev);
    }

    node.setPrev(undefined);
    node.setNext(undefined);

    if (insPrev) {
      insPrev.setInsNext(insNext);
    }

    if (insNext) {
      insNext.setInsPrev(insPrev);
    }

    node.setInsPrev(undefined);
    node.setInsNext(undefined);
  }
}
